import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    ClientDetailsPayloadInterface,
    InteractionPayloadType
} from 'storeTypes'
import { ClientInteractions, InteractionSources } from 'types'
import { useBindDispatch } from './useBindDispatch'
import { constants, isClientInfoComplete } from 'helpers'
import { AppModalContext } from 'components/appModal/AppModalProvider'
import { useNavigate } from 'react-router-dom'
import { buildCardSaleUrl, mobileMoneyPaymentUrl } from 'helpers/buildUrls'

interface UseInteractionsProps {
    client?: ClientDetailsPayloadInterface
    username: string
    villageId: string
    modifyHandler: (modificationType: string, nextUrl?: string) => void
    reEnrollmentHandler?: () => void
}

export const useInteractions = ({
    client,
    username,
    villageId,
    modifyHandler,
    reEnrollmentHandler
}: UseInteractionsProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { sendInteractionsActionHandler, setLastVisitedClientHandler } =
        useBindDispatch()

    const { displayAppModal: showInteractionFeedbackModal } =
        useContext(AppModalContext)

    const cardSaleUrl = useMemo(
        () => buildCardSaleUrl(client?.client_code, villageId),
        [client?.client_code, villageId]
    )

    const mobileMoneyUrl = useMemo(
        () => mobileMoneyPaymentUrl(client?.client_code, villageId),
        [client?.client_code, villageId]
    )

    const interactionHandler = (interaction: InteractionPayloadType) => {
        if (!client) {
            return
        }

        sendInteractionsActionHandler({
            ...interaction,
            client_code: client.client_code,
            user_id: username,
            outcome: interaction.outcome,
            client_status: client.client_status,
            survey_name: constants.TARGET_LIST_SURVEY_NAME,
            survey_version: constants.TARGET_LIST_SURVEY_VERSION,
            village_id: villageId,
            source: InteractionSources.CONNECT
        })

        showInteractionFeedbackModal(t('clientDetails.interactionRecorded'))

        if (
            interaction.name === ClientInteractions.NOT_INTERESTED ||
            interaction.name === ClientInteractions.REQUEST_FOLLOW_UP
        ) {
            if (client?.client_code) {
                setLastVisitedClientHandler(client.client_code)
            }
        }

        if (interaction.name === ClientInteractions.ENROLL) {
            return reEnrollmentHandler?.()
        }

        if (!isClientInfoComplete(client)) {
            const nextUrlByInteraction: Partial<Record<string, string>> = {
                [ClientInteractions.CARD_PAYMENT]: cardSaleUrl,
                [ClientInteractions.MOBILE_MONEY_PAYMENT]: mobileMoneyUrl
            }
            const nextUrl = nextUrlByInteraction[interaction.name ?? ''] || ''

            localStorage.setItem(
                constants.CLIENT_FOR_ENROLLMENT_STORE,
                JSON.stringify(client)
            )

            return modifyHandler(
                constants.CLIENT_MODIFICATION_TYPES.DETAILS,
                nextUrl
            )
        }

        if (interaction.name === ClientInteractions.CARD_PAYMENT) {
            navigate(cardSaleUrl)
        }

        if (interaction.name === ClientInteractions.MOBILE_MONEY_PAYMENT) {
            navigate(mobileMoneyUrl)
        }
    }

    return {
        showInteractionFeedbackModal,
        interactionHandler
    }
}
